// src/features/training/model/trainingSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { requestTrainingPlan, getTrainingStatus, getLatestTrainingPlan } from '../api/trainingApi';

export interface TrainingDay {
    dayOfWeek: number;
    DayTime?: string;
    durationOfTraining?: number;
    Exercises?: Record<string, string>;
    rest?: boolean;
}

export interface TrainingPlan {
    request_id: number;
    status: string;
    created_at: string;
    finished_at: string;
    error: string | null;
    plan: {
        days: TrainingDay[];
    };
}

interface TrainingState {
    plan: TrainingPlan | null;
    loading: boolean;
    error: string | null;
}

const initialState: TrainingState = {
    plan: null,
    loading: false,
    error: null,
};

export const fetchLatestTrainingPlan = createAsyncThunk(
    'training/fetchLatest',
    async () => await getLatestTrainingPlan()
);

export const pollTrainingPlan = createAsyncThunk(
    'training/pollTrainingPlan',
    async (request_id: number, { rejectWithValue }) => {
        while (true) {
            const res = await getTrainingStatus(request_id);

            if (res.status === 'failed') {
                return rejectWithValue('План тренировок не удалось сформировать.');
            }

            if (res.status === 'completed') return res;

            await new Promise(resolve => setTimeout(resolve, 1500));
        }
    }
);

export const requestAndPollTrainingPlan = createAsyncThunk(
    'training/requestAndPoll',
    async (instanceId: number, { dispatch }) => {
        const { request_id } = await requestTrainingPlan(instanceId);
        return await dispatch(pollTrainingPlan(request_id)).unwrap();
    }
);

const trainingSlice = createSlice({
    name: 'training',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchLatestTrainingPlan.fulfilled, (state, action) => {
                state.plan = action.payload;
            })
            .addCase(requestAndPollTrainingPlan.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(requestAndPollTrainingPlan.fulfilled, (state, action) => {
                state.plan = action.payload;
                state.loading = false;
            })
            .addCase(requestAndPollTrainingPlan.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Ошибка при получении тренировочного плана';
            });
    },
});

export default trainingSlice.reducer;
